import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Loading from '../../../components/common/Loading.component';
import Back from '../../../components/common/Back.component';
import { fetchAPI } from '../../../api/request';
import Alert from '../../../components/common/Alert.component';
import { API as listAPI } from '../../../module/list/api';
import _ from 'lodash';
import { API as listItemsDepot } from '../../../module/listItemsDepot/api';
import itemsDepot from '../../../module/listItemsDepot';
import ProductsListComponent from '../../../components/depot/products/ProductsList.component';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ModalLogbook from '../../../components/logbook/modal/ModalLogbook.component';
import { SimpleCreateDepotConf } from '../../../module/logbook/conf/SimpleCreateDepot.conf';
import depot from '../../../module/depot';
import ItemAdd from '../../../components/listItem/create';
import PaperTypography from '../../../components/common/PaperTypography.component';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/Download';
import EditList from '../../../components/list/edit/EditList.component';
import CardInfoOperation from '../../../components/operations/CardInfoOperation.component';

const STATUS_SEND_LOGBOOK = 'DELIVERY YACHT';
const STATUS_LOGBOOK = 'DELIVERED';

const ItemsDepotListPage = ({ module: { query, detail, code } }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, id_operation } = useParams();
  const { jwt } = useSelector((state) => state.login);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(null);
  const [list, setList] = useState(null);
  const [expanded, setExpanded] = useState({ products: true, logbook: true });
  const { loading: loadingAPI, data } = useSelector(detail.selector.data);
  const [checked, setChecked] = useState(null);
  const [idLogbook, setIdLogbook] = useState(null);
  const _query = useSelector(query.all.selector);
  const [download, setDownload] = useState(false);

  useEffect(() => {
    if (data?.query2[0]?.attributes?.id_logbook?.data?.id) {
      setIdLogbook(data?.query2[0]?.attributes?.id_logbook?.data);
    }
    if (data?.query) {
      const allItems = data?.query;
      setChecked(_.map(allItems, 'id'));
    }
  }, [data, success]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded({ ...expanded, [panel]: isExpanded });
  };

  const onError = useCallback((error) => {
    setSuccess(false);
    setLoading(false);
    setError(error);
    // eslint-disable-next-line
  }, []);

  const handleDownload = useCallback((resp) => {
    setError(false);
    setLoading(false);
    const csvBlob = new Blob([resp], { type: 'text/csv;charset=utf-8' });
    // Salva e scarica il file CSV
    const nameCSV = `products_list_${dayjs().format('DD-MM-YYYY_HH:mm')}.csv`;
    saveAs(csvBlob, nameCSV);
  }, []);

  const onClick = useCallback(() => {
    setDownload(!download);
    if (idLogbook?.id) {
      setLoading(true);
      setSuccess(false);
      fetchAPI(
        query.CSV.settings,
        {},
        { ..._query, populate: query.all.populate },
        { id_logbook: idLogbook.id },
        jwt
      )
        .then((resp) => handleDownload(resp))
        .catch(onError);
    }
  }, [idLogbook, jwt, _query, handleDownload, query, onError, download]);

  useEffect(() => {
    fetchAPI(listAPI.item, { id }, '', null, jwt)
      .then((resp) => {
        setList(resp?.data?.attributes);
      })
      .catch((error) => onError(error));
    // eslint-disable-next-line
  }, [id, success, jwt]);

  useEffect(() => {
    if (
      list !== null &&
      ((error &&
        (error?.response?.status === 404 || error?.response?.status === 500)) ||
        `${list?.id_operation_list?.data?.id}` !== `${id_operation}`)
    ) {
      navigate('/404');
    }
  }, [error, navigate, id_operation, list]);

  useEffect(() => {
    dispatch(
      query.detail.action({
        settings: query.detail.settings, //ITEMS LIST
        token: jwt,
        params: { id },
        query: { populate: query.detail.populate },
        query2: { populate: '*' },
        settings2: listItemsDepot.listItemsWithLogbook, //ITEMS IN THE LOGBOOK
      })
    );
    return () => {
      dispatch(
        query.detail.action({
          settings: null,
          token: null,
          params: null,
          query: null,
        })
      );
    };
    // eslint-disable-next-line
  }, [id, success, download]);

  return (
    <Fragment>
      <Container disableGutters={true} maxWidth={false} sx={{ height: '100%' }}>
        {error && <Alert severity='error'>{'An error has occurred'}</Alert>}
        {success && <Alert severity='success'>{'Success'}</Alert>}
        <Back title='Back' page='Items List' />

        {loading || loadingAPI ? (
          <Loading />
        ) : (
          <Stack>
            <CardInfoOperation list={list} isLoading={loading || loadingAPI} />
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              height={{ xs: 'auto', lg: 56 }}
              mt={4}
              mb={4}
              alignItems={{ lg: 'center' }}
            >
              <EditList
                item={list}
                department={code}
                setSuccessEdit={setSuccess}
                successEdit={success}
              />
              {idLogbook?.id ? (
                <Stack direction={'row'} spacing={2} ml={{ xs: 0, md: 2 }}>
                  <Tooltip title='Logbook'>
                    <Link to={`/logbook/${idLogbook?.id}`}>
                      <IconButton
                        sx={{
                          backgroundColor: '#3f51b5',
                          color: '#fff',
                          borderRadius: 1,
                          '& :hover': {
                            color: '#3f51b5',
                          },
                        }}
                      >
                        <LibraryBooksIcon />
                      </IconButton>
                    </Link>
                  </Tooltip>
                  <Button
                    variant='contained'
                    startIcon={<DownloadIcon />}
                    sx={{ minWidth: 150, height: 40 }}
                    onClick={onClick}
                  >
                    <Typography>Items List</Typography>
                  </Button>
                </Stack>
              ) : (
                <Box width={170} ml={2}>
                  <ModalLogbook
                    disabled={_.isEmpty(data?.query)}
                    conf={SimpleCreateDepotConf}
                    setSuccess={setSuccess}
                    productsList={
                      data &&
                      _.concat(
                        _.map(data?.query, 'id'),
                        _.map(data?.query2, id)
                      )
                    }
                    setChecked={setChecked}
                    operationType={STATUS_SEND_LOGBOOK}
                    module={depot}
                  />
                </Box>
              )}
            </Stack>

            {idLogbook?.attributes?.status_logbook !== STATUS_LOGBOOK && (
              <Box sx={{ mb: 1 }}>
                <ItemAdd
                  setSuccessAdd={setSuccess}
                  module={{
                    ...itemsDepot,
                    create: {
                      conf: idLogbook?.id
                        ? itemsDepot.create.conf.hasLogbook
                        : itemsDepot.create.conf.default,
                    },
                  }}
                  idLogbook={idLogbook?.id}
                  productsList={data && _.concat(data?.query, data?.query2)}
                  viewIconAdd={true}
                />
              </Box>
            )}

            {_.isEmpty(data?.query) && !idLogbook?.id && (
              <PaperTypography title={'Add Items to list'} />
            )}
            {idLogbook?.id ? (
              data?.query2.length !== 1 ? (
                <ProductsListComponent
                  data={data?.query2}
                  checked={checked}
                  expanded={expanded}
                  setChecked={setChecked}
                  setSuccess={setSuccess}
                  handleChange={handleChange}
                  title='Sent to Logbook'
                />
              ) : (
                <ProductsListComponent
                  data={[{ ...data?.query2[0], disabledDelete: true }]}
                  checked={checked}
                  expanded={expanded}
                  setChecked={setChecked}
                  setSuccess={setSuccess}
                  handleChange={handleChange}
                  title='Sent to Logbook'
                />
              )
            ) : (
              !_.isEmpty(data?.query) && (
                <ProductsListComponent
                  data={data?.query}
                  checked={checked}
                  expanded={expanded}
                  setChecked={setChecked}
                  setSuccess={setSuccess}
                  handleChange={handleChange}
                />
              )
            )}
          </Stack>
        )}
      </Container>
    </Fragment>
  );
};

export default ItemsDepotListPage;
